// Главная: стена логотипов продуктов в шапке [data-provide="product-wall"].
//
// Появление плиток и «дыхание» делает CSS (Styles/Pages/_home.scss). Скрипт
// отвечает за отклик на курсор: плитка под курсором поднимается, соседи
// поднимаются тем слабее, чем дальше они от курсора, а вся стена слегка
// наклоняется к курсору. Пока курсора нет, «курсор» сам обходит плитки
// в случайном порядке (spotlight) и под стеной показывается название продукта.
//
// Значения не переключаются, а плавно подтягиваются к целевым (lerp в
// requestAnimationFrame) — поэтому движение не дёргается при быстрых
// перемещениях мыши. Цикл останавливается, когда всё улеглось, сцена вне
// экрана или вкладка скрыта. При prefers-reduced-motion скрипт не запускается.

(function () {
    "use strict";

    const SPOTLIGHT_MS = 2800;
    // Даём отыграть анимации появления (последняя плитка стартует с задержкой).
    const START_DELAY_MS = 1800;
    // Радиус влияния «курсора» в размерах плитки и максимальный наклон стены.
    const RADIUS = 1.7;
    const MAX_TILT_DEG = 7;
    // Доля пути к цели за кадр: меньше — плавнее и медленнее.
    const EASE = 0.1;
    const EPSILON = 0.002;

    const wall = document.querySelector<HTMLElement>('[data-provide="product-wall"]');
    if (!wall || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
    }

    const root: HTMLElement = wall;
    const caption = root.querySelector<HTMLElement>(".product-wall-caption");
    const cells = Array.from(root.querySelectorAll<HTMLElement>(".product-wall-cell"));
    const tiles = cells.map((cell) => cell.querySelector<HTMLElement>(".product-wall-tile")!);
    if (tiles.length === 0) {
        return;
    }

    interface Point {
        x: number;
        y: number;
    }

    // Центры ячеек считаем по ячейкам, а не по плиткам: плитки сами смещены в 3D.
    let centers: Point[] = [];
    let size = 1;

    const k = tiles.map(() => 0);
    let a = 0;
    let tx = 0;
    let ty = 0;

    let focus: Point | null = null; // clientX/clientY
    let tiltTarget: Point = { x: 0, y: 0 };
    let hovering = false;
    let visible = true;
    let frame = 0;
    let queue: number[] = [];
    let last = -1;
    let shown = -1;

    function measure(): void {
        centers = cells.map((cell) => {
            const r = cell.getBoundingClientRect();
            return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
        });
        size = cells[0].getBoundingClientRect().width || 1;
    }

    function nearest(point: Point): number {
        let best = 0;
        let min = Infinity;
        centers.forEach((c, i) => {
            const d = Math.hypot(c.x - point.x, c.y - point.y);
            if (d < min) {
                min = d;
                best = i;
            }
        });
        return best;
    }

    function setCaption(index: number): void {
        if (!caption || index === shown) {
            return;
        }

        shown = index;
        if (index < 0) {
            caption.classList.remove("is-visible");
        } else {
            caption.textContent = tiles[index].dataset.name ?? "";
            caption.classList.add("is-visible");
        }
    }

    function step(): void {
        frame = 0;

        const targetA = focus ? 1 : 0;
        a += (targetA - a) * EASE;
        tx += (tiltTarget.x - tx) * EASE;
        ty += (tiltTarget.y - ty) * EASE;
        let moving =
            Math.abs(targetA - a) > EPSILON ||
            Math.abs(tiltTarget.x - tx) > EPSILON ||
            Math.abs(tiltTarget.y - ty) > EPSILON;

        for (let i = 0; i < tiles.length; i++) {
            let target = 0;
            if (focus) {
                const d = Math.hypot(centers[i].x - focus.x, centers[i].y - focus.y) / size;
                const t = Math.max(0, 1 - d / RADIUS);
                target = t * t * (3 - 2 * t); // smoothstep
            }

            k[i] += (target - k[i]) * EASE;
            if (Math.abs(target - k[i]) > EPSILON) {
                moving = true;
            }
            tiles[i].style.setProperty("--k", k[i].toFixed(4));
        }

        root.style.setProperty("--a", a.toFixed(4));
        root.style.setProperty("--tx", `${tx.toFixed(3)}deg`);
        root.style.setProperty("--ty", `${ty.toFixed(3)}deg`);

        if (moving) {
            request();
        }
    }

    function request(): void {
        if (!frame && visible && !document.hidden) {
            frame = requestAnimationFrame(step);
        }
    }

    function pickNext(): number {
        if (queue.length === 0) {
            queue = tiles.map((_, i) => i).sort(() => Math.random() - 0.5);
            if (queue[queue.length - 1] === last && queue.length > 1) {
                queue.unshift(queue.pop()!);
            }
        }
        return queue.pop()!;
    }

    function spotlight(): void {
        if (hovering || !visible || document.hidden) {
            return;
        }

        last = pickNext();
        focus = { ...centers[last] };
        tiltTarget = { x: 0, y: 0 };
        setCaption(last);
        request();
    }

    function release(): void {
        focus = null;
        tiltTarget = { x: 0, y: 0 };
        setCaption(-1);
        request();
    }

    function syncPause(): void {
        root.classList.toggle("is-paused", document.hidden || !visible);
        request();
    }

    root.addEventListener("pointermove", (event) => {
        if (event.pointerType !== "mouse") {
            return;
        }

        if (!hovering) {
            hovering = true;
            measure();
        }

        focus = { x: event.clientX, y: event.clientY };
        setCaption(nearest(focus));

        const r = root.getBoundingClientRect();
        const nx = (event.clientX - (r.left + r.width / 2)) / (r.width / 2);
        const ny = (event.clientY - (r.top + r.height / 2)) / (r.height / 2);
        tiltTarget = {
            x: Math.max(-1, Math.min(1, -ny)) * MAX_TILT_DEG,
            y: Math.max(-1, Math.min(1, nx)) * MAX_TILT_DEG
        };
        request();
    });

    root.addEventListener("pointerleave", (event) => {
        if (event.pointerType === "mouse") {
            hovering = false;
            release();
        }
    });

    // Клавиатура: фокус на ссылке работает как наведение на её плитку.
    root.addEventListener("focusin", (event) => {
        const index = tiles.indexOf((event.target as Element).closest<HTMLElement>(".product-wall-tile")!);
        if (index >= 0) {
            hovering = true;
            measure();
            focus = { ...centers[index] };
            tiltTarget = { x: 0, y: 0 };
            setCaption(index);
            request();
        }
    });

    root.addEventListener("focusout", () => {
        hovering = false;
        release();
    });

    window.addEventListener("resize", () => {
        measure();
        request();
    });

    document.addEventListener("visibilitychange", syncPause);

    if ("IntersectionObserver" in window) {
        new IntersectionObserver((entries) => {
            visible = entries[entries.length - 1].isIntersecting;
            if (visible) {
                measure();
            }
            syncPause();
        }).observe(root);
    }

    measure();
    window.setTimeout(() => {
        measure();
        spotlight();
        window.setInterval(spotlight, SPOTLIGHT_MS);
    }, START_DELAY_MS);
})();
